import type { ComputedRef, Ref } from "vue";
import type { Event, TicketType, EventSession } from "~/types/event";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";

/**
 * useEventPublicData — fetch SSR + disponibilité de l'événement pour la page
 * publique /b/[slug]. Extrait 1:1 de `pages/b/[slug]/index.vue` (Zone A
 * split >800 lignes).
 *
 * "Événement non disponible" UNIQUEMENT sur 404 explicite. Erreurs
 * transitoires (timeout/5xx/network) → skeleton + retry au lieu d'écran rouge.
 */
export async function useEventPublicData(
  slug: ComputedRef<string> | Ref<string>,
  isPreview: ComputedRef<boolean> | Ref<boolean>,
) {
  const { t } = useI18n();
  const config = useRuntimeConfig();

  // SSR data fetch
  const demo = useDemoAccueil().estIntegree;
  const fetchUrl = computed(() =>
    urlApiPublique(`${config.public.apiBaseUrl}/api/public/events/${slug.value}`, {
      preview: isPreview.value,
      demo,
    }),
  );
  // ⚠️ Pas de `await` inline : les computed sont créés de façon SYNCHRONE
  // (avant tout await) pour rester rattachés au scope du composant.
  // L'`await` SSR est fait tout en bas du composable.
  const eventAsync = useFetch<{ success: boolean; data: Event & { ticket_types: TicketType[]; sessions: EventSession[] } }>(
    fetchUrl.value,
    {
      // La clé PORTE le mode aperçu : sans lui, la charge SSR mise en cache
      // pour la page publique serait réutilisée dans l'iframe d'aperçu (et
      // inversement), et un événement en brouillon resterait invisible.
      // Démo de la home : clé à part, rendu du serveur repris (cf. useDonneeDemoAccueil).
      ...useDonneeDemoAccueil(`event-${slug.value}${isPreview.value ? "-preview" : ""}`),
      server: true,
      lazy: false,
      // Retry transitoire (timeout/5xx) pour éviter le flaky "Événement non
      // disponible" au premier refresh (Karim 2026-06-21).
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  );
  const { data: eventData, error: fetchError, pending: isLoadingEvent } = eventAsync;

  // APERÇU TEMPS RÉEL — le dashboard pousse le brouillon en cours d'édition
  // par postMessage ; il se superpose aux données enregistrées. Inerte hors
  // `?preview=true`. Parité adhésion. Karim 2026-08-30.
  const draftPreview = useEntityDraftPreview({
    isPreviewMode: computed(() => isPreview.value === true),
  });

  const event = computed(() => {
    const ssr = eventData.value?.data ?? null;
    const merged = draftPreview.mergeEntity(ssr);
    if (!merged) return null;
    // Les types de billets sont une LISTE : un tarif supprimé dans le
    // dashboard doit disparaître de l'aperçu, d'où le remplacement plutôt
    // qu'une fusion de surface.
    const draftTypes = draftPreview.overrides.value.tarifs;
    if (!draftTypes) return merged;
    return {
      ...merged,
      ticket_types: draftPreview.mergeTarifs(merged.ticket_types ?? []),
    };
  });
  const isInitialLoading = computed(() => isLoadingEvent.value && !event.value);

  const isEntityMissing = computed(() => {
    if (event.value) return false;
    if (!fetchError.value) return false;
    const status = (fetchError.value as { statusCode?: number; status?: number }).statusCode
      ?? (fetchError.value as { statusCode?: number; status?: number }).status;
    return status === 404;
  });

  // Raison réelle du 404 exposée par le backend (canceled / archived / draft /
  // not_found) → on affiche la VÉRITÉ (« annulé ») au lieu du message générique
  // « pas encore publié ou archivé ». Karim 2026-07-02.
  const missingReason = computed<string>(() => {
    const data = (fetchError.value as { data?: { reason?: string } } | null)?.data;
    return data?.reason ?? "not_found";
  });
  const missingCopy = computed(() => {
    switch (missingReason.value) {
      case "canceled":
        return {
          icon: "i-lucide-calendar-x",
          title: t("billetterie.useEventPublicData.annuleTitre"),
          message: t("billetterie.useEventPublicData.annuleMessage"),
        };
      case "archived":
        return {
          icon: "i-lucide-archive",
          title: t("billetterie.useEventPublicData.archiveTitre"),
          message: t("billetterie.useEventPublicData.archiveMessage"),
        };
      case "draft":
        return {
          icon: "i-lucide-clock",
          title: t("billetterie.useEventPublicData.brouillonTitre"),
          message: t("billetterie.useEventPublicData.brouillonMessage"),
        };
      default:
        return {
          icon: "i-lucide-calendar-off",
          title: t("billetterie.useEventPublicData.introuvableTitre"),
          message: t("billetterie.useEventPublicData.introuvableMessage"),
        };
    }
  });

  // Await SSR (cf. note plus haut).
  await eventAsync;

  return {
    event,
    isInitialLoading,
    isEntityMissing,
    missingCopy,
  };
}
